// Public portfolio content. Keep prose and relationships here so every view
// reads the same material. Stage labels are a reading order, not a dated history.
export const themes = [
  { slug: 'working-memory', title: 'Working memory' },
  { slug: 'retrieval', title: 'Retrieval' },
  { slug: 'provenance', title: 'Sources and provenance' },
  { slug: 'interfaces', title: 'Readable interfaces' },
  { slug: 'agent-systems', title: 'Agent systems' },
  { slug: 'authoring', title: 'Creative authoring' },
  { slug: 'verification', title: 'Verification' }
];

export const notes = [
  {
    slug: 'the-swap-test',
    title: 'The swap test',
    summary: 'A tool proves its worth when the workflow still holds up without the strongest model.',
    body: [
      'I tried to rebuild NotebookLM. It only worked when a strong model was driving.',
      'Now I judge tools by what is left when you swap in a weaker agent, or a tired human.'
    ],
    stage: 'Catch the fall',
    featured: true,
    projectSlugs: ['loom', 'ghostnote', 'digital-drafter'],
    themeSlugs: ['agent-systems', 'verification'],
    relatedSlugs: ['ai-degrades-gradually', 'agents-should-leave-programs-behind']
  },
  {
    slug: 'ai-degrades-gradually',
    title: 'AI degrades gradually',
    summary: 'Tools should still have a coarser gear when the best model is unavailable.',
    body: [
      'AI access is not on or off. The model you like gets rationed, replaced, or slowed down.',
      'Tools should degrade like a progressive image: a coarse version first, sharper when a better model is available.'
    ],
    stage: 'Build for rough weather',
    projectSlugs: ['loom', 'digital-drafter'],
    themeSlugs: ['agent-systems', 'verification'],
    relatedSlugs: ['the-swap-test', 'agents-should-leave-programs-behind']
  },
  {
    slug: 'agents-should-leave-programs-behind',
    title: 'Agents should leave programs behind',
    summary: 'The best model work leaves behind something useful after the chat is gone.',
    body: [
      'The best thing a strong model can leave behind is a small program that does not need a model to run.',
      'A chat answer helps once. A script, template, checklist, or test can keep helping after the model is gone.'
    ],
    stage: 'Keep what outlives the chat',
    projectSlugs: ['loom', 'world-authoring-lab', 'digital-drafter'],
    themeSlugs: ['agent-systems', 'authoring', 'verification'],
    relatedSlugs: ['the-swap-test', 'messy-notes-disposable-analysis']
  },
  {
    slug: 'search-vs-saved-objects',
    title: 'Save the thing itself',
    summary: 'Searching old chats is different from opening something you saved on purpose.',
    body: [
      'Searching old chats is archaeology.',
      'Opening something I saved on purpose is picking up a tool.'
    ],
    stage: 'Save the thing itself',
    projectSlugs: ['loom', 'ghostnote'],
    themeSlugs: ['working-memory', 'retrieval'],
    relatedSlugs: ['resemblance-is-not-history', 'messy-notes-disposable-analysis']
  },
  {
    slug: 'resemblance-is-not-history',
    title: 'Resemblance is not history',
    summary: 'Similarity search can find related material without explaining what happened first.',
    body: [
      'Similarity search finds things that look alike.',
      'It cannot tell you what happened first, what caused what, or what replaced it.'
    ],
    stage: 'Trace what actually happened',
    projectSlugs: ['loom', 'ghostnote'],
    themeSlugs: ['retrieval', 'provenance'],
    relatedSlugs: ['search-vs-saved-objects', 'messy-notes-disposable-analysis']
  },
  {
    slug: 'messy-notes-disposable-analysis',
    title: 'Messy notes, disposable analysis',
    summary: 'Keep the source material raw; make the layer on top cheap to rebuild.',
    body: [
      'The notes can stay messy.',
      'The analysis on top of them should be cheap to throw away.'
    ],
    stage: 'Leave the raw notes messy',
    projectSlugs: ['loom', 'ghostnote'],
    themeSlugs: ['working-memory', 'provenance'],
    relatedSlugs: ['search-vs-saved-objects', 'agents-should-leave-programs-behind']
  },
  {
    slug: 'expired-tasks',
    title: 'Expired tasks',
    summary: 'Not every delayed task becomes more urgent.',
    body: [
      'A task that says "shower" from three days ago is not overdue.',
      'It is expired.'
    ],
    stage: 'Know when to let tasks die',
    featured: true,
    projectSlugs: ['loom'],
    themeSlugs: ['working-memory', 'interfaces'],
    relatedSlugs: ['messy-notes-disposable-analysis']
  },
  {
    slug: 'thesaurus',
    title: 'Outside the model bubble',
    summary: 'Traditional media helps keep the language and ideas from collapsing into model defaults.',
    body: [
      'Models tend to converge on similar words and ideas.',
      'Books, journalism, and other conventional media give me more variation and help me avoid getting stuck in a model bubble.'
    ],
    stage: 'Pick your own words',
    featured: true,
    projectSlugs: ['loom', 'ghostnote'],
    themeSlugs: ['authoring', 'verification'],
    relatedSlugs: ['unchecked-answers-spread']
  },
  {
    slug: 'unchecked-answers-spread',
    title: 'Catch mistakes before they spread',
    summary: 'A wrong answer gets harder to correct once people start building on it.',
    body: [
      'A wrong model answer does not just waste your time.',
      'Once someone repeats it in a meeting, everyone has to argue around it.'
    ],
    stage: 'Catch mistakes before they spread',
    projectSlugs: ['loom', 'digital-drafter'],
    themeSlugs: ['verification', 'agent-systems'],
    relatedSlugs: ['thesaurus', 'the-swap-test']
  }
];

export const projects = [
  {
    slug: 'loom',
    title: 'Loom',
    homeTitle: 'Loom + GhostNote',
    kicker: 'Memory and retrieval',
    summary: 'A local system for keeping notes, tasks, source records, and follow-up work connected.',
    homeSummary: 'A flexible workspace that adjusts to your ideas. Save notes, documents, tasks, and sources, then come back to them anytime.',
    heading: 'Local memory for notes, tasks, and sources',
    body: [
      'Loom is a local memory system for work that spreads across chats, notes, source material, and follow-up tasks.',
      'GhostNote is the reading surface for that material: drafts, research notes, architecture sketches, transcripts, and source packets.'
    ],
    problem: 'Like many people, I write half-sentences, unfinished ideas, and tasks I forget to cross out. Loom captures ideas midflight and preserves enough context to make the material readable later.',
    approach: [
      'Loom preserves the context around notes, documents, tasks, and sources.',
      'GhostNote lets you browse and refine that material as drafts, research notes, or deployable architecture.'
    ],
    demonstrates: [
      'Together they work like a personal research lab: capture messy material, keep the source nearby, and return when it is ready to become something clearer.',
      'A SQLite-backed record system for tasks, logs, insights, scratch documents, and source references.',
      'A shared core used through CLI and MCP interfaces.'
    ],
    openQuestion: 'How much structure can the system add before capture starts feeling like filling out paperwork?',
    themeSlugs: ['working-memory', 'retrieval', 'provenance'],
    technologyEvidence: [
      { name: 'C# / .NET', detail: 'A shared core applies lifecycle and retrieval rules through the CLI and MCP interfaces.' },
      { name: 'SQLite / full-text search', detail: 'A durable store keeps captured records available for retrieval and source inspection.' },
      { name: 'Model Context Protocol', detail: 'Agents can work with the same stored material and application rules as the CLI.' }
    ],
    noteSlugs: [
      'the-swap-test',
      'search-vs-saved-objects',
      'resemblance-is-not-history',
      'messy-notes-disposable-analysis',
      'expired-tasks',
      'thesaurus'
    ],
    accent: 'cyan'
  },
  {
    slug: 'ghostnote',
    title: 'GhostNote',
    kicker: 'A readable workspace',
    summary: 'A workspace for reading notes, documents, timelines, and source packets without opening the whole archive every time.',
    heading: 'A workspace for reading messy project context',
    body: [
      'GhostNote is where captured material becomes easier to browse: notes, documents, transcripts, drafts, logs, and source packets.',
      'It is meant for returning to unfinished work without rebuilding the whole situation from scratch.'
    ],
    problem: 'Saving everything created a second problem: there was more material, but not always a clear view of what was current, nearby, or worth reading next.',
    approach: [
      'Show the current record first, then keep nearby source material, history, and related tasks close enough to recover the thread.',
      'Use transcripts, chronological logs, tags, archive browsing, and document views as different ways into the same material.'
    ],
    demonstrates: [
      'A .NET and Blazor workspace for documents, transcripts, tags, logs, and archives.',
      'A navigation pattern built around the current record plus nearby context.',
      'The difference between preserving records and making them readable.'
    ],
    openQuestion: 'How can nearby context stay easy to reach without making every page feel like a junk drawer?',
    themeSlugs: ['working-memory', 'interfaces', 'provenance'],
    technologyEvidence: [
      { name: '.NET / Blazor', detail: 'The workspace brings chat, documents, tags, chronological logs, and archive browsing into one application.' },
      { name: 'OpenAI Whisper', detail: 'Transcription makes recordings available as text within the workspace.' },
      { name: 'Local text classification', detail: 'A lightweight local classification pipeline supports routine organization.' }
    ],
    noteSlugs: [
      'search-vs-saved-objects',
      'resemblance-is-not-history',
      'messy-notes-disposable-analysis',
      'thesaurus'
    ],
    accent: 'violet',
    showOnHome: false
  },
  {
    slug: 'world-authoring-lab',
    title: 'World Authoring Lab',
    kicker: 'Procedural worlds and creative tools',
    summary: 'A prototype for terrain, materials, reusable parts, and editor controls for scenes you can keep changing.',
    heading: 'Small tools for editable generated worlds',
    body: [
      'World Authoring Lab is a prototype for procedural terrain, asset composition, materials, and small editor surfaces.',
      'The point is less “generate a scene” and more “can the scene still be edited after the first pass?”'
    ],
    problem: 'One-off generated demos can look impressive and still be dead ends.',
    approach: [
      'Use explicit terrain recipes, materials, reusable parts, and scene state.',
      'Build small controls for adjusting generated candidates instead of treating the first output as final.',
      'Use visual QA to see what changed instead of trusting that the generated scene still works.'
    ],
    demonstrates: [
      'Terrain, materials, and asset composition as editable state.',
      'Small editor surfaces for steering generated worlds.',
      'The boundary between model-assisted authoring and actual world state.'
    ],
    openQuestion: 'What are the few controls that make a generated scene worth continuing?',
    themeSlugs: ['authoring', 'interfaces', 'verification'],
    technologyEvidence: [
      { name: 'Procedural terrain', detail: 'Terrain experiments explore adjustable recipes and inspectable scene state.' },
      { name: 'Materials and scene components', detail: 'Material experiments and reusable parts support variation without rebuilding the whole scene.' },
      { name: 'Editor surfaces', detail: 'Small tools make terrain, assets, and visual choices easier to steer and review.' }
    ],
    noteSlugs: [
      'agents-should-leave-programs-behind'
    ],
    accent: 'green'
  },
  {
    slug: 'digital-drafter',
    title: 'Digital Drafter',
    kicker: 'An earlier coding-agent prototype',
    summary: 'A local prototype for testing the loop around model-written code: inspect, change, run, recover, verify.',
    homeSummary: 'An early local coding agent that could inspect files, edit a repo, run commands on your computer, and record what happened.',
    heading: 'An early coding-agent workbench',
    body: [
      'Digital Drafter was an early local prototype for working with coding models.',
      'The useful part was not proving that a model could write code. It was seeing what had to happen around the model before a repo change could be trusted.'
    ],
    problem: 'Generated code is just one step. Repository work also needs access to files, build output, tool results, Git state, and recovery paths.',
    approach: [
      'Give models typed tools for files, Git repositories, project structure, builds, commands, and web retrieval.',
      'Coordinate those tool calls through a bounded loop with stored results between steps.'
    ],
    demonstrates: [
      'A quick prototype for the inspect, edit, run, and record loop behind coding agents.',
      'Why logging tool results, failures, and recovery steps matters.',
      'How quickly cost, observability, and retry behavior become part of the product.'
    ],
    openQuestion: 'How much of the next step should an agent explain before it takes it?',
    themeSlugs: ['agent-systems', 'verification'],
    technologyEvidence: [
      { name: 'C# / .NET', detail: 'Application logic coordinates the execution loop and tools for inspecting and building .NET projects.' },
      { name: 'OpenAI / Anthropic APIs', detail: 'Model integrations share strongly typed JSON tools for repository operations.' },
      { name: 'Git automation', detail: 'Repository tools expose Git operations as part of the bounded agent loop.' }
    ],
    noteSlugs: [
      'the-swap-test',
      'ai-degrades-gradually',
      'agents-should-leave-programs-behind',
      'unchecked-answers-spread'
    ],
    accent: 'amber',
    homeCompact: true
  }
];

export const skillGroups = [
  {
    id: 'applications-systems',
    title: 'Applications and systems',
    items: [
      {
        title: 'C# and .NET',
        description: 'Web applications, internal tools, REST APIs, ASP.NET Core, EF Core, and WPF.'
      },
      {
        title: 'Web interfaces',
        description: 'Angular, TypeScript, JavaScript, React, HTML, and CSS; authentication and accessible interfaces.'
      },
      {
        title: 'Automation and integrations',
        description: 'Migration tooling, including a small domain-specific language, and telephony integrations that replaced manual steps.'
      },
      {
        title: 'Services and data',
        description: 'SQL and PostgreSQL; sensor data carried into reports; billing services I made 6.5x faster.'
      },
      {
        title: 'Delivery and maintenance',
        description: 'Azure, Docker, Git/GitHub, Jenkins, Octopus Deploy, and IIS.'
      }
    ]
  },
  {
    id: 'agentic-systems',
    title: 'LLMs and agents',
    origin: 'Personal projects',
    items: [
      {
        title: 'Agents with limits',
        description: 'OpenAI and Anthropic APIs; agents that read files, edit repositories, and run builds, with logs and caps on retries and cost.'
      },
      {
        title: 'Context and retrieval',
        description: 'Prompt and context design, document search, saved history, and output checks in C#/Blazor workspaces.'
      },
      {
        title: 'Speech and audio',
        description: 'Whisper transcription and text-to-speech playback.'
      },
      {
        title: 'Natural language and ML',
        description: 'Simple machine-learning tools that sort and tag documents without calling an LLM.'
      },
      {
        title: 'Python and ML tools',
        description: 'Python, TensorFlow, PyTorch, Keras, Pandas, NumPy, and Jupyter.'
      }
    ]
  },
  {
    id: 'courses',
    title: 'Courses',
    items: [
      {
        title: 'Neural Networks and Deep Learning',
        description: '2018'
      },
      {
        title: 'Introduction to TensorFlow for Artificial Intelligence, Machine Learning, and Deep Learning',
        description: '2019'
      },
      {
        title: 'Convolutional Neural Networks in TensorFlow',
        description: '2019'
      }
    ]
  }
];

export const getProject = (slug) => projects.find((project) => project.slug === slug);

export const getNote = (slug) => notes.find((note) => note.slug === slug);

// Curated project order comes first. Newly added notes appear automatically
// through projectSlugs, so a tiny note only needs to be authored once.
export const getProjectNotes = (slug) => {
  const project = getProject(slug);
  if (!project) return [];

  const slugs = [...new Set([
    ...(project.noteSlugs || []),
    ...notes.filter((note) => note.projectSlugs.includes(slug)).map((note) => note.slug)
  ])];

  return slugs.map(getNote).filter(Boolean);
};

export const getRelatedNotes = (slug) => {
  const note = getNote(slug);
  if (!note) return [];

  return [...new Set(note.relatedSlugs || [])]
    .filter((relatedSlug) => relatedSlug !== slug)
    .map(getNote)
    .filter(Boolean)
    .slice(0, 4);
};
